// 广告管理工具

// 扩展全局Window接口
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

export class AdManager {
  private static instance: AdManager;
  private adSlots: Map<string, boolean> = new Map();
  private observers: Map<string, IntersectionObserver> = new Map();

  static getInstance(): AdManager {
    if (!AdManager.instance) {
      AdManager.instance = new AdManager();
    }
    return AdManager.instance;
  }

  // 注册广告位
  registerAdSlot(slot: string): void {
    this.adSlots.set(slot, false);
  }

  // 标记广告位为已加载
  markAdLoaded(slot: string): void {
    this.adSlots.set(slot, true);
    console.log(`Ad slot ${slot} loaded successfully`);
  }

  // 检查广告位是否已加载
  isAdLoaded(slot: string): boolean {
    return this.adSlots.get(slot) || false;
  }

  // 创建广告可见性观察器
  createVisibilityObserver(slot: string, element: HTMLElement, callback: () => void): void {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 广告进入视口，延迟加载以确保更好的展示效果
            setTimeout(() => {
              callback();
            }, 100);
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.1, // 10%可见时触发
        rootMargin: '50px' // 提前50px开始加载
      }
    );

    observer.observe(element);
    this.observers.set(slot, observer);
  }

  // 清理观察器
  cleanupObserver(slot: string): void {
    const observer = this.observers.get(slot);
    if (observer) {
      observer.disconnect();
      this.observers.delete(slot);
    }
  }

  // 获取广告展示统计
  getAdStats(): { total: number; loaded: number; loading: number } {
    const total = this.adSlots.size;
    const loaded = Array.from(this.adSlots.values()).filter(Boolean).length;
    return {
      total,
      loaded,
      loading: total - loaded
    };
  }

  // 重置所有广告位状态
  reset(): void {
    this.adSlots.clear();
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }
}

// 全局广告管理器实例
export const adManager = AdManager.getInstance();

// 广告加载工具函数
export const loadAd = (slot: string): void => {
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
    adManager.markAdLoaded(slot);
  } catch (error) {
    console.error(`Failed to load ad slot ${slot}:`, error);
  }
};

// 检查广告脚本是否已加载
export const isAdScriptLoaded = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof window.adsbygoogle !== 'undefined';
};

// 等待广告脚本加载
export const waitForAdScript = (): Promise<void> => {
  return new Promise((resolve) => {
    if (isAdScriptLoaded()) {
      resolve();
      return;
    }

    const checkScript = () => {
      if (isAdScriptLoaded()) {
        resolve();
      } else {
        setTimeout(checkScript, 100);
      }
    };

    checkScript();
  });
}; 